import type { BoardLayer, Keyboard, KeyboardLayout, QmkBoardLayer } from "@/domain/types";
import { AZERTY_CODE_TO_CHAR, AZERTY_CODE_TO_TOKEN } from "./azerty";
import { COLEMAK_CODE_TO_CHAR } from "./colemak";
import { DVORAK_CODE_TO_CHAR } from "./dvorak";
import { legendFor } from "./legends";
import { PORTUGUESE_CODE_TO_CHAR, PORTUGUESE_CODE_TO_TOKEN } from "./portuguese";
import { QGMLWB_CODE_TO_CHAR } from "./qgmlwb";
import { QGMLWY_CODE_TO_CHAR } from "./qgmlwy";
import { QWERTY_CODE_TO_CHAR } from "./qwerty";
import { QWERTZ_CODE_TO_CHAR, QWERTZ_CODE_TO_TOKEN } from "./qwertz";
import { SCANDINAVIAN_CODE_TO_CHAR, SCANDINAVIAN_CODE_TO_TOKEN } from "./scandinavian";
import { SPANISH_CODE_TO_CHAR, SPANISH_CODE_TO_TOKEN } from "./spanish";
import { SWISS_CODE_TO_CHAR, SWISS_CODE_TO_TOKEN } from "./swiss";
import { TURKISH_Q_CODE_TO_CHAR, TURKISH_Q_CODE_TO_TOKEN } from "./turkish-q";
import { UK_COLEMAK_CODE_TO_CHAR, UK_COLEMAK_CODE_TO_TOKEN } from "./uk-colemak";
import { UK_QWERTY_CODE_TO_CHAR, UK_QWERTY_CODE_TO_TOKEN } from "./uk-qwerty";

// The ISO 102nd key (VK_OEM_102, code IntlBackslash) sits outside every table's US-Qwerty code
// block, so mk itself appends its char at assembly - a REQUIRED parameter, so a newly added
// layout cannot silently ship without the key (its cap would go blank and its char binding
// silently drop on the ISO boards). Each ISO-aware table's own doc comment names what the key
// emits ("\" on the UK/US-derived layouts, "<" on the continental ones). Appending keeps map
// order, so a table's own key stays the first-wins charToCode winner for a shared char.
const mk = (
  id: string,
  name: string,
  map: Record<string, string>,
  iso102: "\\" | "<",
  tokens?: Record<string, string>,
  overflow?: Record<string, string>,
): KeyboardLayout => {
  const full = { ...map, IntlBackslash: iso102 };
  return {
    id,
    name,
    map: full,
    ...(tokens ? { tokens } : {}),
    ...(overflow ? { overflow } : {}),
  };
};

// The ISO boards remap the US backslash key onto Enter (their tokens tables carry
// Backslash: "Enter"), so an incoming BackSlash-derived [Hotkeys] bind can collide with a native
// Enter bind; the incoming one serializes to the overflow key instead (decision 0045). The UK
// target (Apostrophe) and the Swiss target (OEM8) are witnessed in TheCore's own editions 1-4
// per-locale files (which relocated the chat family to those keys); UK Colemak follows UK QWERTY
// by symmetry, unwitnessed (no archived UK Colemak file exercises the collision).
const UK_OVERFLOW: Record<string, string> = { Enter: "Apostrophe" };
const SWISS_OVERFLOW: Record<string, string> = { Enter: "OEM8" };

// On the US-derived layouts the 102nd key duplicates the Backslash char, so it needs its own
// serialization token to stay code-addressable past the first-wins char reverse. "OEM102"
// follows the witnessed VK naming convention (OEM8 for VK_OEM_8 in TheCore's own UK files);
// inferred from the VK name, not yet witnessed in a real .SC2Hotkeys export.
const US_ISO_TOKENS: Record<string, string> = { IntlBackslash: "OEM102" };

export const qwerty = mk("qwerty", "QWERTY", QWERTY_CODE_TO_CHAR, "\\", US_ISO_TOKENS);
export const ukQwerty = mk(
  "uk-qwerty",
  "UK QWERTY",
  UK_QWERTY_CODE_TO_CHAR,
  "\\",
  UK_QWERTY_CODE_TO_TOKEN,
  UK_OVERFLOW,
);
export const dvorak = mk("dvorak", "Dvorak", DVORAK_CODE_TO_CHAR, "\\", US_ISO_TOKENS);
export const colemak = mk("colemak", "Colemak", COLEMAK_CODE_TO_CHAR, "\\", US_ISO_TOKENS);
export const ukColemak = mk(
  "uk-colemak",
  "UK Colemak",
  UK_COLEMAK_CODE_TO_CHAR,
  "\\",
  UK_COLEMAK_CODE_TO_TOKEN,
  UK_OVERFLOW,
);
export const azerty = mk("azerty", "AZERTY", AZERTY_CODE_TO_CHAR, "<", AZERTY_CODE_TO_TOKEN);
export const qwertz = mk(
  "qwertz",
  "QWERTZ (German)",
  QWERTZ_CODE_TO_CHAR,
  "<",
  QWERTZ_CODE_TO_TOKEN,
);
export const swiss = mk(
  "swiss",
  "Swiss (QWERTZ)",
  SWISS_CODE_TO_CHAR,
  "<",
  SWISS_CODE_TO_TOKEN,
  SWISS_OVERFLOW,
);
export const spanish = mk(
  "spanish",
  "Spanish (LatAm & Spain)",
  SPANISH_CODE_TO_CHAR,
  "<",
  SPANISH_CODE_TO_TOKEN,
);
export const portuguese = mk(
  "portuguese",
  "Portuguese",
  PORTUGUESE_CODE_TO_CHAR,
  "<",
  PORTUGUESE_CODE_TO_TOKEN,
);
export const scandinavian = mk(
  "scandinavian",
  "Scandinavian",
  SCANDINAVIAN_CODE_TO_CHAR,
  "<",
  SCANDINAVIAN_CODE_TO_TOKEN,
);
export const turkishQ = mk(
  "turkish-q",
  "Turkish-Q",
  TURKISH_Q_CODE_TO_CHAR,
  "<",
  TURKISH_Q_CODE_TO_TOKEN,
);
export const qgmlwb = mk("qgmlwb", "QGMLWB", QGMLWB_CODE_TO_CHAR, "\\", US_ISO_TOKENS);
export const qgmlwy = mk("qgmlwy", "QGMLWY", QGMLWY_CODE_TO_CHAR, "\\", US_ISO_TOKENS);

/**
 * The universal mappings the picker offers when no custom board is active, in display order.
 * Grouping/UX polish for the longer list is deferred (out-of-scope).
 */
export const universalLayouts: KeyboardLayout[] = [
  qwerty,
  ukQwerty,
  dvorak,
  colemak,
  ukColemak,
  azerty,
  qwertz,
  swiss,
  spanish,
  portuguese,
  scandinavian,
  turkishQ,
  qgmlwb,
  qgmlwy,
];

const REGISTRY: Record<string, KeyboardLayout> = Object.fromEntries(
  universalLayouts.map((l) => [l.id, l]),
);

export function getLayout(id: string): KeyboardLayout | undefined {
  return REGISTRY[id];
}

/** Returns the emitted char for a code in this layout, or null if the key emits no character. */
export function codeToChar(layout: KeyboardLayout, code: string): string | null {
  return layout.map[code] ?? null;
}

/**
 * The single code for a char (case-insensitive for letters), or null. The first emitting
 * key wins, matching the SC2 parser's first-binding rule; use charToCodes when a mapping can
 * repeat a char across physical keys - a projected board layer, or any universal layout's
 * ISO-102nd alias (Backslash and IntlBackslash both emit "\" on the US-derived tables, the
 * table's own key first). One reverse mechanism, one winner rule.
 */
export function charToCode(layout: KeyboardLayout, char: string): string | null {
  return charToCodes(layout, char)[0] ?? null;
}

const reverseAllCache = new WeakMap<KeyboardLayout, Record<string, string[]>>();

/**
 * Every code that emits a char in this layout, in map order. A char can have several
 * emitters: a board layer projected through charMapOf repeats keycodes across physical keys,
 * and every US-derived universal layout deliberately aliases "\" onto the ISO 102nd key
 * (Backslash first, IntlBackslash second - twin-paint on ISO boards). Callers that must pick
 * one key need the full candidate list, not an arbitrary winner.
 */
export function charToCodes(layout: KeyboardLayout, char: string): string[] {
  let rev = reverseAllCache.get(layout);
  if (!rev) {
    rev = {};
    for (const [code, ch] of Object.entries(layout.map)) {
      const key = ch.toUpperCase();
      const list = rev[key] ?? [];
      list.push(code);
      rev[key] = list;
    }
    reverseAllCache.set(layout, rev);
  }
  return rev[char.toUpperCase()] ?? [];
}

/**
 * Whether the active mapping is a board layer (KLE or VIA/QMK, position-keyed) rather than a
 * universal Keyboard Layout (code-keyed). The one place that knows the structural discriminator
 * between the two mapping families, so no consumer spells the shape test itself.
 */
export function isBoardLayerMapping(mapping: KeyboardLayout | BoardLayer): mapping is BoardLayer {
  return "chars" in mapping;
}

/**
 * The keycode position i emits through this mapping - the ACTIVE code that the resolved layers,
 * the rendered cap and physical input all key by. A universal layout has no remap, so the active
 * code IS the geometry code (KeyboardEvent.code); a keycoded (VIA/QMK) layer carries its resolved
 * active code per position, where null is a DEAD cap (KC_NO, a layer switch) that must never
 * inherit the geometry code - that would paint another key's binding onto a blank cap; a
 * keycodeless KLE layer has no active keycode, so the geometry code stands in.
 */
export function activeCodeOf(
  keyboard: Keyboard,
  mapping: KeyboardLayout | BoardLayer,
  i: number,
): string | null {
  if (isBoardLayerMapping(mapping) && mapping.kind === "qmk") return mapping.codes[i] ?? null;
  return keyboard.keys[i]?.code ?? null;
}

/**
 * The display legend position i shows through this mapping. A keycoded layer carries a
 * precomputed legend per position - the active keycode's identity (a gaming-layer thumb Ctrl
 * reads "Ctrl", a layer-switch "MO3"), where "" is a genuinely empty cell, never a fallthrough.
 * A keycodeless KLE layer has no per-position legend, so its char stands in, then the physical
 * code's fixed label. A universal mapping is code-keyed: the emitted char, else the fixed label.
 */
export function legendOf(
  keyboard: Keyboard,
  mapping: KeyboardLayout | BoardLayer,
  i: number,
): string {
  const geoCode = keyboard.keys[i]?.code;
  if (isBoardLayerMapping(mapping)) {
    return (
      (mapping.kind === "qmk" ? mapping.legends[i] : null) ??
      mapping.chars[i] ??
      (geoCode ? legendFor(geoCode) : "")
    );
  }
  if (geoCode == null) return "";
  return codeToChar(mapping, geoCode) ?? legendFor(geoCode);
}

/**
 * View any active mapping as a code-to-char table. Universal layouts pass through; a board layer's
 * position-keyed chars project onto each key's ACTIVE code - the code it emits through this layer
 * so the SC2 table this feeds shares one code space with the projection (a remapped "9"
 * keys on Digit9, not the gaming key's layer-0 code). A keycodeless KLE board layer has no active
 * keycode, so it falls back to the geometry code. First key wins on a shared code: a char emitted
 * from two keys maps once, and the projection twin-paints it onto both at render. Gives the SC2
 * parser one shape to resolve characters against.
 */
export function charMapOf(
  keyboard: Keyboard,
  mapping: KeyboardLayout | BoardLayer,
): KeyboardLayout {
  if (!isBoardLayerMapping(mapping)) return mapping;
  const map: Record<string, string> = {};
  mapping.chars.forEach((ch, i) => {
    const code = activeCodeOf(keyboard, mapping, i);
    if (ch != null && code && !(code in map)) map[code] = ch;
  });
  // a derived layer (TheCore Plus, always QMK-kinded) carries its underlying layout's VK-token
  // table and ISO overflow; the char table must keep them, or parsing the served (converted)
  // text through this map silently drops every national OEM and 0045-displaced bind. Real
  // VIA/QMK layers carry neither, so they are unchanged; KLE layers have no token fields.
  const tokens = mapping.kind === "qmk" ? mapping.tokens : undefined;
  const overflow = mapping.kind === "qmk" ? mapping.overflow : undefined;
  return {
    id: mapping.id,
    name: mapping.name,
    map,
    ...(tokens ? { tokens } : {}),
    ...(overflow ? { overflow } : {}),
  };
}

/**
 * The mapping as a keycoded board layer (a VIA/QMK board layer carrying per-key keycodes), or null
 * for a universal Keyboard Layout or a single-layer KLE board (neither carries keycodes). Returns
 * the narrowed keycodes-present type for a consumer that branches a whole path on it - the
 * projector picks its token resolution route with this. The per-position question (which code
 * does position i emit) belongs to activeCodeOf, not here.
 */
export function keycodedBoardLayer(mapping: KeyboardLayout | BoardLayer): QmkBoardLayer | null {
  return isBoardLayerMapping(mapping) && mapping.kind === "qmk" ? mapping : null;
}
